/** 
 * RoTextarea - 表单文本域 Web 组件
 * 支持：默认/选中/错误/禁用、浮动标签、前后图标、多种校验、表单关联 API
 * 校验文案：分桶属性或 window.RoFormLocale（见 snippets/ro-form-locale.liquid）
 * 用法：<ro-textarea placeholder="Description" float-label required rows="4"></ro-textarea>
 */
class RoTextarea extends HTMLElement {
  static formAssociated = true;

  /** 需要同步到内部 textarea 的属性名 */
  static TEXTAREA_ATTRS = ["pattern", "min", "max", "rows", "cols"];

  static DEFAULT_MESSAGES = {
    required: "This field is required",
    pattern: "Invalid format",
    min_length: "Minimum {{ min }} characters required",
    max_length: "Maximum {{ max }} characters allowed",
  };

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._internals = this.attachInternals();

    this._value = "";
    this._isFocused = false;
    this._isError = false;
    this._errorMessage = "";

    this._onInput = this._onInput.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onContainerClick = this._onContainerClick.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);

    this._render();
  }

  _render() {
    const textareaId = `ro-textarea-${Math.random().toString(36).slice(2, 9)}`;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-flex;
          flex-direction: column;
          width: 100%;
        }
        .textarea-container {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 16px;
          background: var(--bg-white-color, #fff);
          border: 1px solid var(--border-color, #e1e1e1);
          border-radius: 6px;
          transition: border-color 0.2s ease;
          cursor: text;
        }
        :host([focused]) .textarea-container { border-color: var(--main-color, #161616); }
        :host([error]) .textarea-container { border-color: var(--error-color, #d93840); }
        :host([disabled]) .textarea-container {
          background: rgba(225, 225, 225, 0.4);
          cursor: not-allowed;
        }
        .icon-slot {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          flex-shrink: 0;
          color: var(--second-color, #6c6d6e);
          margin-top: 4px;
        }
        .icon-slot.empty { display: none; }
        .icon-slot::slotted(*) { width: 24px; height: 24px; display: block; }
        .textarea-wrapper {
          flex: 1;
          min-width: 0;
          position: relative;
          display: flex;
          flex-direction: column;
          min-height: 32px;
        }
        :host([float-label]) .textarea-container {
          box-sizing: border-box;
        }
        :host([float-label][has-value]) .textarea-wrapper,
        :host([float-label][focused]) .textarea-wrapper { justify-content: flex-start; }
        .label {
          position: absolute;
          left: 0;
          top: 4px;
          color: var(--second-color, #6c6d6e);
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          pointer-events: none;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }
        :host([float-label][has-value]) .label,
        :host([float-label][focused]) .label {
          position: static;
          font-size: 12px;
          line-height: 16px;
          margin-bottom: 4px;
        }
        .textarea {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          color: var(--main-color, #161616);
          font-weight: 400;
          font-size: 16px;
          line-height: 1.5;
          padding: 0;
          margin: 0;
          resize: vertical;
          min-height: 80px;
          font-family: inherit;
        }
        .textarea::placeholder {
          font-size: 14px;
          font-weight: 400;
        }
        :host(:not([float-label])) .textarea::placeholder {
          color: var(--second-color, #6c6d6e);
          opacity: 1;
        }
        :host([float-label]) .textarea::placeholder { opacity: 0; }
        .textarea:disabled {
          cursor: not-allowed;
          color: var(--second-color, #6c6d6e);
        }
        .error-message {
          display: none;
          margin-top: 8px;
          padding: 0 16px;
          color: var(--error-color, #d93840);
          font-weight: 400;
          font-size: var(--error-font-size, 12px);
          line-height: var(--error-line-height, 16px);
        }
        :host([error]) .error-message { display: block; }
        @media (max-width: 1183px) {
          .textarea-container { padding: 12px; }
          .error-message { padding: 0 12px; }
        }
      </style>
      <div class="textarea-container">
        <slot name="prefix-icon" class="icon-slot"></slot>
        <div class="textarea-wrapper">
          <label class="label" for="${textareaId}"></label>
          <textarea class="textarea" id="${textareaId}"></textarea>
        </div>
        <slot name="suffix-icon" class="icon-slot"></slot>
      </div>
      <div class="error-message"></div>
    `;

    this._textareaEl = this.shadowRoot.querySelector(".textarea");
    this._labelEl = this.shadowRoot.querySelector(".label");
    this._errorEl = this.shadowRoot.querySelector(".error-message");
    this._containerEl = this.shadowRoot.querySelector(".textarea-container");
    this._prefixSlot = this.shadowRoot.querySelector('slot[name="prefix-icon"]');
    this._suffixSlot = this.shadowRoot.querySelector('slot[name="suffix-icon"]');
  }

  connectedCallback() {
    this._textareaEl.addEventListener("input", this._onInput);
    this._textareaEl.addEventListener("focus", this._onFocus);
    this._textareaEl.addEventListener("blur", this._onBlur);
    this._textareaEl.addEventListener("change", this._onChange);
    this._containerEl.addEventListener("click", this._onContainerClick);
    this._prefixSlot.addEventListener("slotchange", this._onSlotChange);
    this._suffixSlot.addEventListener("slotchange", this._onSlotChange);

    this._updateTextareaAttributes();
    this._updateLabel();
    this._updateSlotVisibility(this._prefixSlot);
    this._updateSlotVisibility(this._suffixSlot);
  }

  disconnectedCallback() {
    this._textareaEl.removeEventListener("input", this._onInput);
    this._textareaEl.removeEventListener("focus", this._onFocus);
    this._textareaEl.removeEventListener("blur", this._onBlur);
    this._textareaEl.removeEventListener("change", this._onChange);
    this._containerEl.removeEventListener("click", this._onContainerClick);
    this._prefixSlot.removeEventListener("slotchange", this._onSlotChange);
    this._suffixSlot.removeEventListener("slotchange", this._onSlotChange);
  }

  static get observedAttributes() {
    return [
      "placeholder", "value", "disabled", "required", "float-label", "name",
      "pattern", "min-length", "max-length", "min", "max", "rows", "cols",
      "validate-fn", "validate-on-input",
      "error-message", "error-message-required", "error-message-invalid",
    ];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (["disabled", "required", "pattern", "min", "max", "min-length", "max-length", "name", "rows", "cols"].includes(name)) {
      this._updateTextareaAttributes();
      return;
    }
    if (["placeholder", "float-label"].includes(name)) {
      this._updateLabel();
      return;
    }
    if (name === "value") {
      this._updateValue(newValue);
      return;
    }
    if (["error-message", "error-message-required", "error-message-invalid"].includes(name) && this._isError) {
      this.validate();
    }
  }

  // ---------- 事件处理 ----------

  _onInput(e) {
    this._value = e.target.value;
    this.setAttribute("value", this._value);
    this._updateHasValueState();
    this._internals.setFormValue(this._value);
    this._dispatch("input", { value: this._value });
    if (this.hasAttribute("validate-on-input")) this.validate();
  }

  _onFocus() {
    this._isFocused = true;
    this.setAttribute("focused", "");
    this._dispatch("focus", { value: this._value });
  }

  _onBlur() {
    this._isFocused = false;
    this.removeAttribute("focused");
    if (!this.hasAttribute("validate-on-input")) this.validate();
    this._dispatch("blur", { value: this._value });
  }

  _onChange() {
    this._dispatch("change", { value: this._value });
  }

  /** 点击容器空白区域时聚焦文本域（避免点击图标也触发） */
  _onContainerClick(e) {
    if (this.hasAttribute("disabled")) return;
    const t = e.target;
    if (t.classList.contains("textarea-container") || t.classList.contains("textarea-wrapper") || t.classList.contains("label")) {
      this._textareaEl.focus();
    }
  }

  /** slot 内容变化时更新图标占位显示/隐藏 */
  _onSlotChange(e) {
    this._updateSlotVisibility(e.target);
  }

  /** 派发自定义事件 */
  _dispatch(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  // ---------- 公共方法 ----------

  /** 执行校验，更新错误状态并派发 validate/error 事件 */
  validate() {
    if (this.hasAttribute("disabled")) return { valid: true, message: "" };

    const errorMessage =
      this._validateRequired() ??
      this._validatePattern() ??
      this._validateLength() ??
      this._validateCustom() ??
      "";

    const valid = !errorMessage;
    this._setError(!valid, errorMessage);
    this._dispatch("validate", { valid, message: errorMessage, value: this._value });
    if (!valid) this._dispatch("error", { message: errorMessage, value: this._value });
    return { valid, message: errorMessage };
  }

  reset() {
    this._value = "";
    this._textareaEl.value = "";
    this.setAttribute("value", "");
    this._setError(false, "");
    this._updateHasValueState();
    this._internals.setFormValue("");
  }

  clearError() {
    this._setError(false, "");
  }

  focus() {
    this._textareaEl.focus();
  }

  blur() {
    this._textareaEl.blur();
  }

  // ---------- 校验规则（按优先级，返回错误文案或 null） ----------

  _validateRequired() {
    if (!this.hasAttribute("required") || this._value.trim()) return null;
    return this._getErrorMessage("required");
  }

  _validatePattern() {
    if (!this._value || !this.hasAttribute("pattern")) return null;
    const pattern = new RegExp(this.getAttribute("pattern"));
    return !pattern.test(this._value) ? this._getErrorMessage("pattern") : null;
  }

  _validateLength() {
    if (!this._value) return null;
    const min = this.getAttribute("min-length");
    const max = this.getAttribute("max-length");
    if (min && this._value.length < parseInt(min, 10)) {
      return this._getErrorMessage("min_length", { min });
    }
    if (max && this._value.length > parseInt(max, 10)) {
      return this._getErrorMessage("max_length", { max });
    }
    return null;
  }

  _validateCustom() {
    if (!this._value || !this.hasAttribute("validate-fn")) return null;
    const fn = window[this.getAttribute("validate-fn")];
    return typeof fn === "function" ? fn(this._value) : null;
  }

  // ---------- 辅助方法 ----------

  /** 根据 slot 是否有插槽内容切换 empty 类，控制占位是否显示 */
  _updateSlotVisibility(slotEl) {
    const hasContent = slotEl.assignedNodes({ flatten: true }).some(n => n.nodeType === Node.ELEMENT_NODE);
    slotEl.classList.toggle("empty", !hasContent);
  }

  /** 将宿主属性同步到内部 textarea 与 ARIA */
  _updateTextareaAttributes() {
    this._textareaEl.disabled = this.hasAttribute("disabled");
    this._textareaEl.required = this.hasAttribute("required");
    this._textareaEl.name = this.getAttribute("name") || "";
    RoTextarea.TEXTAREA_ATTRS.forEach(attr => {
      const val = this.getAttribute(attr);
      if (val != null) this._textareaEl[attr] = val;
    });
    const minLen = this.getAttribute("min-length");
    const maxLen = this.getAttribute("max-length");
    if (minLen != null) this._textareaEl.minLength = parseInt(minLen, 10);
    if (maxLen != null) this._textareaEl.maxLength = parseInt(maxLen, 10);
    this._updateAriaAttributes();
  }

  /** 根据 float-label 决定 placeholder 显示在 label 还是 textarea */
  _updateLabel() {
    const text = this.getAttribute("placeholder") || "";
    if (this.hasAttribute("float-label")) {
      this._labelEl.textContent = text;
      this._textareaEl.placeholder = "";
    } else {
      this._labelEl.textContent = "";
      this._textareaEl.placeholder = text;
    }
  }

  _updateValue(value) {
    this._value = value ?? "";
    this._textareaEl.value = this._value;
    this._updateHasValueState();
    this._internals.setFormValue(this._value);
  }

  _updateHasValueState() {
    this.toggleAttribute("has-value", !!this._value);
  }

  _setError(isError, message) {
    this._isError = isError;
    this._errorMessage = message;
    this.toggleAttribute("error", isError);
    this._errorEl.textContent = isError ? (this.getAttribute("error-message") || message) : "";
    this._updateAriaAttributes();
  }

  _updateAriaAttributes() {
    this._textareaEl.toggleAttribute("aria-invalid", this._isError);
    this._textareaEl.toggleAttribute("aria-required", this.hasAttribute("required"));
    if (this._isError && this._errorMessage) {
      const id = this._errorEl.id || `error-${Math.random().toString(36).slice(2)}`;
      this._errorEl.id = id;
      this._textareaEl.setAttribute("aria-describedby", id);
    } else {
      this._textareaEl.removeAttribute("aria-describedby");
    }
  }

  _applyParams(msg, params = {}) {
    if (msg == null || msg === "") return msg;
    let out = String(msg);
    if (params && Object.keys(params).length) {
      Object.entries(params).forEach(([k, v]) => {
        out = out.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, "g"), String(v));
      });
    }
    return out;
  }

  /** 与 ro-input 一致：legacy → 分桶 → RoFormLocale → DEFAULT_MESSAGES */
  _getErrorMessage(key, params = {}) {
    const legacy = this.getAttribute("error-message");
    if (legacy) return this._applyParams(legacy, params);

    if (key === "required") {
      const a = this.getAttribute("error-message-required");
      if (a != null && a !== "") return this._applyParams(a, params);
    } else {
      const a = this.getAttribute("error-message-invalid");
      if (a != null && a !== "") return this._applyParams(a, params);
    }

    let msg = window.RoFormLocale?.[key];
    if (msg != null && msg !== "") return this._applyParams(msg, params);

    msg = RoTextarea.DEFAULT_MESSAGES[key];
    if (msg != null && msg !== "") return this._applyParams(msg, params);
    return "";
  }

  // ---------- 属性访问器 ----------

  get value() { return this._value; }
  set value(v) { this.setAttribute("value", v); }
  get disabled() { return this.hasAttribute("disabled"); }
  set disabled(v) { this.toggleAttribute("disabled", !!v); }
  get required() { return this.hasAttribute("required"); }
  set required(v) { this.toggleAttribute("required", !!v); }

  // ---------- 表单关联 API ----------

  formResetCallback() {
    this.value = "";
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
  }

  formStateRestoreCallback(state, mode) {
    if (mode === "restore") this.value = state;
  }
}

customElements?.define("ro-textarea", RoTextarea);